import { TextApplianceResult, TextEditingResult
       } from '../../text/text.controller';
import { TextEditingRefusalReason
       } from '../../text/text.controller.methods';

export enum RejectionReason {
  TextTooHigh,
  TextTooWide,
  TextTooLong,
  TextTooSmall,
  CharacterWontFit,
  FontSizeTooLarge,
  FontSizeTooSmall,
  FontFamilyTooLarge,
  InvalidCharacters,
  Unspecified,
}

export interface RejectionOptions {
  flexible: boolean;
  zoomMode: string;
}

export function getRejectionMessage(
    reason: RejectionReason,
    options: RejectionOptions = {
      flexible: false,
      zoomMode: 'fit'
    }): string | undefined
{
  switch (reason) {
    case RejectionReason.TextTooHigh:
      if (options.flexible) {
        return 'i18n.control.textEditing.rejectMessage.resizeTextField';
      }
      return 'i18n.control.textEditing.rejectMessage.maxLinesReached';
    case RejectionReason.TextTooWide:
      if (options.flexible && options.zoomMode === 'fit') {
        return 'i18n.control.textEditing.rejectMessage.insertLineBreakOrResizeTextField'; // eslint-disable-line
      }
      else if (options.zoomMode === 'limit') {
        return 'i18n.control.textEditing.rejectMessage.maxCharactersReached';
      }
      return 'i18n.control.textEditing.rejectMessage.insertLineBreak';
    case RejectionReason.TextTooLong:
      return 'i18n.control.textEditing.rejectMessage.maxCharactersReached';
    case RejectionReason.CharacterWontFit:
      return 'i18n.control.textEditing.rejectMessage.characterWontFit';
    case RejectionReason.FontFamilyTooLarge:
      return 'i18n.control.textEditing.rejectMessage.fontFamilyTooLarge';
    case RejectionReason.FontSizeTooLarge:
      return 'i18n.control.textEditing.rejectMessage.fontSizeTooLarge';
    case RejectionReason.FontSizeTooSmall:
      return 'i18n.control.textEditing.rejectMessage.fontSizeTooSmall';
    case RejectionReason.InvalidCharacters:
      return 'i18n.control.textEditing.rejectMessage.invalidCharacters';

    default: return undefined;
  }
}

export enum EditingAction {
  ChangeText,
  ChangeFont,
  ChangeFontSize,
  ChangeEffects,
  Unspecified,
}

export function determineFeedbackMessage(
    action: EditingAction,
    result: TextEditingResult,
    options: RejectionOptions): string | undefined
{
  if ([undefined, TextEditingRefusalReason.None].indexOf(result.reason) !== -1)
  {
    return undefined;
  }

  switch (action) {
    case EditingAction.ChangeText:
    case EditingAction.ChangeEffects:
      if (result.type === TextApplianceResult.Success) {
        return undefined;
      }
      else {
        if (result.reason === TextEditingRefusalReason.TextTooHigh) {
          return getRejectionMessage(RejectionReason.TextTooHigh, options);
        }

        if (result.reason === TextEditingRefusalReason.TextTooWide) {
          return getRejectionMessage(RejectionReason.TextTooWide, options);
        }

        if (result.reason === TextEditingRefusalReason.TextTooLong) {
          return getRejectionMessage(RejectionReason.TextTooLong);
        }

        if (result.reason === TextEditingRefusalReason.CharacterNotSupported) {
          return getRejectionMessage(RejectionReason.InvalidCharacters);
        }

        return getRejectionMessage(RejectionReason.Unspecified);
      }

    case EditingAction.ChangeFontSize:
      return getRejectionMessage(
        result.reason === TextEditingRefusalReason.MinFontSizeUndershot
          ? RejectionReason.FontSizeTooSmall
          : RejectionReason.FontSizeTooLarge);

    case EditingAction.ChangeFont:
      if (result.type === TextApplianceResult.Success) {
        return undefined;
      }
      else {
        if (result.reason === TextEditingRefusalReason.CharacterNotSupported) {
          return getRejectionMessage(RejectionReason.InvalidCharacters);
        } else {
          return getRejectionMessage(RejectionReason.FontFamilyTooLarge);
        }
      }

    default:
      return getRejectionMessage(RejectionReason.Unspecified);
  }
}
